import type { GetRateTableFunction } from './types/GetRateTableFunction';
import type { RateTable } from './types/RateTable';
import type { GetRateTableDependencies } from './types/GetRateTableDependencies';
import { validateRateTable } from './validateRateTable';
import { RateTableInternalError } from './RateTableInternalError';
import { RateTableExternalError } from './RateTableExternalError';

export const getRateTable =
  ({
    baseURL = import.meta.env.VITE_RATE_TABLE_BASE_URL,
    fetcher = fetch,
    reporter,
  }: GetRateTableDependencies): GetRateTableFunction =>
  (path = import.meta.env.VITE_RATE_TABLE_PATH): Promise<RateTable> => {
    let url: string = '';

    try {
      url = new URL(path, baseURL).toString();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // because this is an issue we can potentially resolve by fixing
      // environment variables during build time, we want to report
      // this problem as an error
      const internalError = new RateTableInternalError(error.message);

      reporter.error(internalError, {
        originalException: error,
      });

      return Promise.reject(internalError);
    }

    return fetcher(url)
      .then((response) => response.json())
      .then(validateRateTable)
      .catch((error) => {
        if (error instanceof RateTableInternalError) {
          reporter.error(error, {
            attachments: [
              {
                filename: 'rate-table.json',
                data: JSON.stringify(error.rateTable),
                contentType: 'application/json',
              },
            ],
          });

          return Promise.reject(error);
        }

        // unexpected errors like network connection issues are going
        // to happen from time to time, so we want to report this problem
        // as a warning for now as there is no immediate action we can take
        reporter.warn(`[RATE_TABLE_ERROR] ${error.message}`, {
          originalException: error,
        });

        return Promise.reject(new RateTableExternalError(error.message));
      });
  };
